import {
    applyFiltersUpload,
    applyFiltersDownload,
    generateErrors,
    applyUsableFilters,
    normalizeStringFilter,
    applyTransportPlanLegVoyages,
    withAppliedFilters,
} from 'utilities/util';
import { ThunkDispatch } from 'redux-thunk';
import { IStore } from 'reducers/index';
import { Action } from 'redux';
import { TYPES as FILTER_TYPES } from 'action-types/filters';
import { TYPES } from 'action-types/ets';
import {
    callApiGet,
    callApiPost,
    callApiPostUploadExport,
    callApiV3Post,
    callApiV3Get,
    callApiV2Post,
    callApiV2Get
} from 'utilities/apiCaller';
import API from 'constants/api';
import { displayFilters } from 'actions/filters';
import { TransportMode } from 'constants/modes';

interface IEtsSearch {
    readonly id: number;
    readonly etsNumber: string;
    readonly cblStatus: string;
    readonly finOpsStatus: string;
    readonly sapsoNumber: string;
    readonly equipmentNumber: string;
    readonly cblNumber: string;
    readonly originService: string;
    readonly destinationService: string;
    readonly consigneeBECode: string;
    readonly agedInHrs: number;
    readonly shipperBECode: string;
    readonly carrierCode: string;
    readonly etd: string;
    readonly eta: string;
    readonly lastChangeDate: string;
    readonly lastChangedBy: string;
    readonly rateErrorMessage: string;
    readonly placeOfReceipt: string;
    readonly portOfLoading: string;
    readonly portOfDischarge: string;
    readonly placeOfDelivery: string;
    readonly productType: string;
    readonly source: string;
    readonly salesOrg: string;
    readonly jobCreationDate: string;
    readonly contractualParty: string;
}

interface IBffEtsSearch {
    readonly etsId: number;
    readonly etsNumber: string;
    readonly sapSalesOrderNumber: string;
    readonly equipmentNumber: string;
    readonly cblNumber: string;
    readonly cblStatus: string;
    readonly jobStatus: string;
    readonly consignee: string;
    readonly shipper: string;
    readonly estimatedTimeOfArrival: string;
    readonly estimatedTimeOfDeparture: string;
    readonly lastChangeDate: string;
    readonly lastChangedBy: string;
    readonly aging: number;
    readonly carrierCode: string;
    readonly originService: string;
    readonly destinationService: string;
    readonly placeOfReceipt: string;
    readonly portOfLoading: string;
    readonly portOfDischarge: string;
    readonly placeOfDelivery: string;
    readonly errorMessage: string;
    readonly productType: string;
    readonly source: string;
    readonly salesOrg: string;
    readonly jobCreationDate: string;
    readonly contractualParty: string;
}

export const fetchETS = (pageNumber?: number, pageSize?: number) => {
    return async (
        dispatch: ThunkDispatch<any, any, Action>,
        getState: () => IStore,
    ) => {
        dispatch({ type: TYPES.FETCH_ETS.PENDING });
        displayFilters(false, 'jobCostingFiltersETS');
        try {
            const payload = applyUsableFilters(normalizeStringFilter(getState, 'ReferenceNumbers'), {
                    Page: pageNumber,
                    PageSize: pageSize
                });
            const res = await callApiV2Post(API.ETS.EtsSearch, payload, {});
            dispatch({
                type: FILTER_TYPES.SET_TOTAL_COUNT,
                totalCount: res.data.totalCount,
            });
            // remember the exact request that produced the displayed results so
            // bulk "download all results" collection can replay it page by page
            dispatch({ type: FILTER_TYPES.SET_LAST_SEARCH_PAYLOAD, target: 'ets', payload });
            dispatch({
                type: TYPES.FETCH_ETS.FULFILLED,
                ets: {
                    ...res.data, result: res.data.result.map((search: IBffEtsSearch) => <IEtsSearch>{
                        id: search.etsId,
                        etsNumber: search.etsNumber,
                        cblStatus: search.cblStatus,
                        finOpsStatus: search.jobStatus,
                        sapsoNumber: search.sapSalesOrderNumber,
                        equipmentNumber: search.equipmentNumber,
                        cblNumber: search.cblNumber,
                        originService: search.originService,
                        destinationService: search.destinationService,
                        consigneeBECode: search.consignee,
                        agedInHrs: search.aging,
                        shipperBECode: search.shipper,
                        carrierCode: search.carrierCode,
                        etd: search.estimatedTimeOfDeparture,
                        eta: search.estimatedTimeOfArrival,
                        lastChangeDate: search.lastChangeDate,
                        lastChangedBy: search.lastChangedBy,
                        rateErrorMessage: search.errorMessage,
                        placeOfReceipt: search.placeOfReceipt,
                        portOfLoading: search.portOfLoading,
                        portOfDischarge: search.portOfDischarge,
                        placeOfDelivery: search.placeOfDelivery,
                        productType: search.productType,
                        salesOrg: search.salesOrg,
                        source: search.source,
                        jobCreationDate: search.jobCreationDate,
                        contractualParty: search.contractualParty,
                    })
                }
            });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_ETS.REJECTED, errors });
        }
    };
};

export const resetETS = () => {
    return async (
        dispatch: ThunkDispatch<any, any, Action>,
        getState: () => IStore,
    ) => {
        dispatch({ type: TYPES.RESET_ETS.PENDING });
        dispatch({ type: FILTER_TYPES.SET_TOTAL_COUNT, totalCount: 0 });
        dispatch({ type: TYPES.RESET_ETS.FULFILLED, ets: [] });
    };
};

/**
 * Fetches a single search page for the "download all results" flow.
 * Replays the exact payload of the last successful grid search (captured in
 * filters.lastSearchPayloads, covering Apply, pagination, and sorting), so
 * collection always matches the displayed results and count. When no payload
 * was captured yet, falls back to the last applied filter snapshot so that
 * edited-but-unapplied inputs still cannot leak into the query. Runs in the
 * background so collection keeps global loading and error indicators out of
 * the singleton store. Returns minimal row objects shaped like grid rows
 * (id = etsId), which is what the bulk download action consumes.
 */
export const fetchEtsPageForDownload = (pageNumber: number, pageSize: number) => {
    return async (
        _dispatch: ThunkDispatch<any, any, Action>,
        getState: () => IStore,
    ) => {
        const filtersState = getState().filters || ({} as any);
        const captured = (filtersState.lastSearchPayloads || {}).ets;
        const payload = captured
            ? { ...captured, Page: pageNumber, PageSize: pageSize }
            : applyUsableFilters(normalizeStringFilter(withAppliedFilters(getState), 'ReferenceNumbers'), {
                Page: pageNumber,
                PageSize: pageSize
            });
        const res = await callApiV2Post(API.ETS.EtsSearch, payload, {}, true);
        return ((res.data && res.data.result) || []).map((item: IBffEtsSearch) => ({
            id: item.etsId
        }));
    };
};

export const resetEtsDetails = () => {
    return async (
        dispatch: ThunkDispatch<any, any, Action>,
        getState: () => IStore,
    ) => {
        dispatch({ type: TYPES.RESET_ETS_DETAILS });
    };
};

export const resetEtsDownload = () => {
    return async (
        dispatch: ThunkDispatch<any, any, Action>,
        getState: () => IStore,
    ) => {
        dispatch({ type: TYPES.RESET_DOWNLOAD });
    };
};

export const fetchETSDetails = (documentId: string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_ETS_DETAILS.PENDING });
        try {
            const res = await callApiV3Get(
                `${API.ETS.GetDetails}/${documentId}`,
            );
            dispatch({
                type: TYPES.FETCH_ETS_DETAILS.FULFILLED,
                etsDetails: applyTransportPlanLegVoyages(res.data),
            });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({
                type: TYPES.FETCH_ETS_DETAILS.REJECTED,
                errors,
            });
        }
    };
};

export const bulkUploadETS = (formData: any) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.BULK_UPLOAD_ETS.PENDING });
        try {
            const res = await callApiV3Post(
                `${API.ETS.UploadChargeItems}`,
                formData,
                {
                    headers: {
                        TransportMode:
                            TransportMode.Ocean,
                        'content-type':
                            'multipart/form-data',
                    },
                },
            );
            dispatch({
                type: TYPES.BULK_UPLOAD_ETS.FULFILLED,
                bulkUploadStatus: res.data,
            });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({
                type: TYPES.BULK_UPLOAD_ETS.REJECTED,
                errors,
            });
        }
    };
};

export const bulkUploadExport = (formData: any) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.BULK_UPLOAD_EXPORT.PENDING });
        try {
            const url = API.UAM.GetPermission.split('/finance')[0];
            const res = await callApiPostUploadExport(
                `${url}/bulk-upload/upload`,
                formData,
                {
                    headers: {
                        TransportMode:
                            TransportMode.Ocean,
                        'content-type':
                            'multipart/form-data',
                    },
                },
            );
            dispatch({
                type: TYPES.BULK_UPLOAD_EXPORT.FULFILLED,
                bulkUploadExportResponse: res.data,
            });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({
                type: TYPES.BULK_UPLOAD_EXPORT.REJECTED,
                errors,
            });
        }
    };
};

export const bulkDownloadETS = (documentIds: number[]) => {
    return async (
        dispatch: ThunkDispatch<any, any, Action>,
        getState: () => IStore,
    ) => {
        dispatch({ type: TYPES.BULK_DOWNLOAD_ETS.PENDING });
        try {
            const query: any = {
                ReferenceIds: documentIds,
                UserId: getState().nickname.userNickName
                    .employeeId,
            };
            const res = await callApiPost(
                `${API.ETS.DownloadChargeItems}`,
                query,
                {
                    headers: {
                        TransportMode:
                            TransportMode.Ocean,
                    },
                },
            );
            dispatch({
                type: TYPES.BULK_DOWNLOAD_ETS.FULFILLED,
                bulkDownloadString: res.data,
            });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({
                type: TYPES.BULK_DOWNLOAD_ETS.REJECTED,
                errors,
            });
        }
    };
};

export const bulkDownloadFile = (documentId: string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.BULK_DOWNLOAD_ETS_FILE.PENDING });
        try {
            const res = await callApiGet(
                `${API.ETS.DownloadFile}/${documentId}`,
                {},
                {
                    headers: {
                        'Content-Type': 'blob',
                    },
                    responseType: 'arraybuffer',
                },
            );
            const fileName =
                res.headers['content-disposition'].split(
                    "'",
                )[2];
            dispatch({
                type: TYPES.BULK_DOWNLOAD_ETS_FILE.FULFILLED,
                bulkDownloadFile: res.data,
                bulkDownloadfilename: fileName,
            });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({
                type: TYPES.BULK_DOWNLOAD_ETS_FILE.REJECTED,
                errors,
            });
        }
    };
};

export const fetchUploadedChargeLogs = (
    pageNumber?: number,
    pageSize?: number,
) => {
    return async (
        dispatch: ThunkDispatch<any, any, Action>,
        getState: () => IStore,
    ) => {
        dispatch({ type: TYPES.FETCH_BULK_UPLOAD.PENDING });
        try {
            const res = await callApiV3Post(
                API.ETS.GetUploadedChargeLogs,
                applyFiltersUpload(
                    getState,
                    {
                        isAirInFilters: false,
                        isAirInBody: false,
                    },
                    {
                        Page: pageNumber,
                        PageNumber: pageNumber,
                        PageSize: pageSize,
                    },
                ),
                {},
            );
            dispatch({
                type: FILTER_TYPES.SET_TOTAL_COUNT,
                totalCount: res.data.totalCount,
            });
            dispatch({
                type: TYPES.FETCH_BULK_UPLOAD.FULFILLED,
                bulkUploadLogs: res.data,
            });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({
                type: TYPES.FETCH_BULK_UPLOAD.REJECTED,
                errors,
            });
        }
    };
};

export const fetchDownloadChargeLogs = (
    pageNumber?: number,
    pageSize?: number,
) => {
    return async (
        dispatch: ThunkDispatch<any, any, Action>,
        getState: () => IStore,
    ) => {
        dispatch({ type: TYPES.FETCH_DOWNLOAD_LOGS.PENDING });
        try {
            const res = await callApiPost(
                API.ETS.GetDownloadLogs,
                applyFiltersDownload(
                    getState,
                    {
                        isAirInFilters: false,
                        isAirInBody: false,
                    },
                    {
                        Page: pageNumber,
                        PageNumber: pageNumber,
                        PageSize: pageSize,
                    },
                ),
                {},
            );
            dispatch({
                type: FILTER_TYPES.SET_TOTAL_COUNT,
                totalCount: res.data.totalCount,
            });
            dispatch({
                type: TYPES.FETCH_DOWNLOAD_LOGS.FULFILLED,
                bulkDownloadLogs: res.data,
            });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({
                type: TYPES.FETCH_DOWNLOAD_LOGS.REJECTED,
                errors,
            });
        }
    };
};

export const sendCreateSalesOrderImport = (
    ReferenceId: number,
    DocumentStage: number,
    RateRequestAction: number,
    Category: 'ISSO',
    UserId: string = '',
) => {
    const requestUrl = API.ETS.SendCreateSalesOrderImport;
    const requestObject = {
        Filter: [
            {
                ReferenceId,
                DocumentStage,
                RateRequestAction,
                Category,
                UserId,
            },
        ],
    };
    return (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.SALES_ORDER_IMPORT.PENDING });
        return new Promise((resolve, reject) => {
            callApiV2Post(requestUrl, requestObject)
                .then((res) => {
                    dispatch({
                        type: TYPES.SALES_ORDER_IMPORT.FULFILLED,
                        payload: res.data
                    });
                    resolve(res.data);
                })
                .catch((e) => {
                    dispatch({
                        type: TYPES.SALES_ORDER_IMPORT.REJECTED,
                        payload: 'Error fetching create sales order import',
                    });
                    reject(e);
                });
        });
    };
};

export const fetchNftpBillable = (jobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_NFTP_BILLABLE.PENDING });
        try {
            const res = await callApiV2Get(
                API.ETS.NftpRouter,
                { jobId, messageType: 'SalesOrder' },
                {},
                true,
            );
            dispatch({ type: TYPES.FETCH_NFTP_BILLABLE.FULFILLED, nftpBillableResponse: res.data });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_NFTP_BILLABLE.REJECTED, errors });
        }
    };
};
